import React, { SyntheticEvent, useState } from 'react';
import cx from 'classnames';
import { useNavigate } from 'react-router-dom';
import { Tooltip } from 'antd';

import {
  getNotificationIcon,
  getNotificationRedirectURL,
} from 'utils/notificationUtils';
import {
  removeNotification,
  setNotificationReadStatus,
} from 'redux/reducers/notificationSlice';
import { getDateFromNow } from 'utils/dateUtils';
import BaseAlert from 'components/wrappers/BaseAlert';
import { ApiErrorMessage } from 'constants/ErrorMessages';
import { BackofficeNotificationStreamModel } from 'api/backofficeNotificationApiClient';
import { checkIsErrorNotification } from 'utils/notificationUtils';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';

import styles from '../../HeaderNotifications.module.less';

type Props = {
  notification: BackofficeNotificationStreamModel;
  onNotificationClick: () => void;
};

export default function NotificationCard(props: Props) {
  const { notification, onNotificationClick } = props;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const {
    type: notificationType,
    createdDate,
    notificationId,
    isNew,
    description,
    title,
  } = notification;
  const isAdmin = useAppSelector(isAdminSelector);
  const notificationIcon = getNotificationIcon(notificationType);
  const isErrorNotification = checkIsErrorNotification(notificationType);

  const handleRemoveNotification = async (event: SyntheticEvent) => {
    event.stopPropagation();

    if (isLoading) return;

    setIsLoading(true);

    const result = await dispatch(removeNotification({ notificationId, isUserAdmin:isAdmin }));

    if (!removeNotification.fulfilled.match(result)) {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsLoading(false);
  };

  const markNotificationAsNew = async (isNew: boolean) => {
    if (isLoading) return;

    setIsLoading(true);

    const result = await dispatch(
      setNotificationReadStatus({
        notificationId,
        isNew,
        isUserAdmin: isAdmin
      }),
    );

    if (!setNotificationReadStatus.fulfilled.match(result)) {
      BaseAlert.error(result.error.message || ApiErrorMessage);
    }

    setIsLoading(false);
  };

  const toggleNotificationReadStatus = (event: SyntheticEvent) => {
    event.stopPropagation();

    markNotificationAsNew(!isNew);
  };

  const handleNotificationClick = () => {
    const url = getNotificationRedirectURL(notification);

    isNew && markNotificationAsNew(false);

    if (!url) return;

    onNotificationClick();

    navigate(url);
  };

  return (
    <div
      data-id={notificationId}
      className={cx('notification-card', styles.notifyCard, {
        [styles.notifyCardNew]: isNew,
        [styles.notifyCardError]: isErrorNotification,
      })}
      onClick={handleNotificationClick}
    >
      <Tooltip
        overlayClassName="light-tooltip"
        title={isNew ? 'Mark as read' : 'Mark as unread'}
        trigger={['hover', 'focus']}
        placement="right"
        getPopupContainer={(triggerNode) =>
          triggerNode.parentNode as HTMLElement
        }
      >
        <div
          className={styles.notifyCardPoint}
          onClick={toggleNotificationReadStatus}
        />
      </Tooltip>

      <div className={styles.notifyCardIcon}>{notificationIcon}</div>

      <div className={styles.notifyCardContent}>
        <div className={styles.notifyCardTitle}>{title}</div>

        <div className={styles.notifyCardDescription}>{description}</div>

        <div className={styles.notifyCardTime}>
          <span className="icon icon-clock" />
          <span>{getDateFromNow(createdDate)}</span>
        </div>
      </div>

      <Tooltip
        overlayClassName="light-tooltip"
        title="Remove notification"
        trigger={['hover', 'focus']}
        placement="left"
        getPopupContainer={(triggerNode) =>
          triggerNode.parentNode as HTMLElement
        }
      >
        <div
          className={styles.notifyCardClose}
          onClick={handleRemoveNotification}
        >
          <span className="icon icon-close-sm"></span>
        </div>
      </Tooltip>
    </div>
  );
}
